/*
 * aura-carousel.js: the home carousel.
 * The index under the stage drives a scroll-snap track; swiping the stage works without this file.
 * Picking an item (click, tap, or Tab into it) lights it and slides the stage to its media; swiping
 * the stage lights the item for the slide in view. Only the current slide's video plays, once the page
 * has loaded and while the carousel is on screen; never under reduced motion or Save-Data (the photo
 * stays). Never auto-advances. Tapping the video (or an optional Pause button, [data-carousel-pause];
 * WCAG 2.2.2) stops the looping video until it is tapped again.
 * A slide's video is a URL pair (data-src, data-src-phone) or a theme video set (data-sources, see
 * snippets/video-set.liquid): a ladder of sizes per stage shape. The set picks the cut for the 767px
 * breakpoint and the smallest size that covers the stage at up to 2x density (the smallest on 2G/3G),
 * and reloads in the other cut if the breakpoint is crossed. Until a video is playing, its still shows,
 * so a blocked autoplay or a failed load just leaves the still.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 767px)');
  const loaded = document.readyState === 'complete'
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
  const VIDEO = 'video[data-src], video[data-sources]';
  const slowNet = () => /(^|-)2g$|^3g$/.test(navigator.connection?.effectiveType || '');

  function pickSource(video) {
    if (!video.dataset.sources) return phone.matches ? video.dataset.srcPhone : video.dataset.src;
    const sets = JSON.parse(video.dataset.sources);
    const ladder = (phone.matches ? sets.phone : sets.wide) || sets.wide || sets.phone;   // smallest first
    if (slowNet()) return ladder[0].src;
    const need = video.parentElement.clientWidth * Math.min(window.devicePixelRatio || 1, 2);
    return (ladder.find((r) => r.w >= need) || ladder[ladder.length - 1]).src;
  }

  class AuraCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel__track');
      this.slides = [...this.querySelectorAll('.carousel__slide')];
      this.items = [...this.querySelectorAll('.carousel__item')];
      this.picks = this.items.map((item) => item.querySelector('[data-carousel-dot]'));
      this.index = 0;
      this.target = null;
      this.onScreen = false;
      this.ready = false;

      // A click anywhere on an item (its button covers it) or focus moving into it picks it
      this.items.forEach((item, i) => item.addEventListener('focusin', () => this.go(i)));
      this.picks.forEach((pick, i) => pick?.addEventListener('click', () => this.go(i)));

      // While the stage slides to a picked item, the slides it passes don't take over the index
      this.slideObserver = new IntersectionObserver((entries) => {
        if (this.target !== null) return;
        entries.forEach((e) => { if (e.isIntersecting) this.setCurrent(this.slides.indexOf(e.target)); });
      }, { root: this.track, threshold: 0.6 });
      this.slides.forEach((s) => this.slideObserver.observe(s));
      this.onScrollEnd = () => { this.target = null; clearTimeout(this.settle); };
      this.track.addEventListener('scrollend', this.onScrollEnd);

      this.viewObserver = new IntersectionObserver(([e]) => { this.onScreen = e.isIntersecting; this.syncVideo(); }, { threshold: 0.25 });
      this.viewObserver.observe(this);

      // WCAG 2.2.2: the slide videos loop, so tapping the video pauses it (and an optional Pause button, if the
      // section has one, does the same). Paused, no video plays until tapped again (picking another slide keeps it paused).
      // A swipe doesn't fire a click, so only a tap toggles.
      this.userPaused = false;
      this.pauseBtn = this.querySelector('[data-carousel-pause]');
      this.pauseBtn?.addEventListener('click', () => this.togglePause());
      this.track.addEventListener('click', () => {
        if (this.motionOk() && this.slides[this.index]?.querySelector(VIDEO)) this.togglePause();
      });

      // Crossing the phone breakpoint changes the stage shape, so a set video reloads in the other cut
      this.onBreakpoint = () => {
        this.slides.forEach((s) => {
          const video = s.querySelector('video[data-sources]');
          if (!video?.getAttribute('src')) return;
          video.pause();
          video.classList.remove('is-playing');
          video.removeAttribute('src');
          video.load();
        });
        this.syncVideo();
      };
      phone.addEventListener('change', this.onBreakpoint);

      this.onMotion = () => this.syncVideo();
      reduce.addEventListener('change', this.onMotion);
      this.onBlockSelect = (e) => { const i = this.items.indexOf(e.target); if (i > -1) this.go(i); };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.setCurrent(0);
      loaded.then(() => { this.ready = true; this.syncVideo(); });
    }

    disconnectedCallback() {
      this.slideObserver?.disconnect();
      this.viewObserver?.disconnect();
      clearTimeout(this.settle);
      reduce.removeEventListener('change', this.onMotion);
      phone.removeEventListener('change', this.onBreakpoint);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    go(i) {
      if (i === this.index && this.target === null) return;
      this.target = i;
      this.setCurrent(i);
      const left = i * this.track.clientWidth;
      if (Math.abs(this.track.scrollLeft - left) < 1) { this.target = null; return; }
      // Browsers without scrollend hand the index back to the stage once the slide has had time to land
      clearTimeout(this.settle);
      this.settle = setTimeout(this.onScrollEnd, 900);
      this.track.scrollTo({ left, behavior: reduce.matches ? 'auto' : 'smooth' });
    }

    setCurrent(i) {
      if (i < 0 || i >= this.slides.length) return;
      this.index = i;
      this.items.forEach((item, k) => {
        if (k === i) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      this.picks.forEach((pick, k) => pick?.setAttribute('aria-pressed', String(k === i)));
      this.syncVideo();
    }

    motionOk() {
      return !reduce.matches && !navigator.connection?.saveData;
    }

    togglePause() {
      this.userPaused = !this.userPaused;
      if (this.pauseBtn) {
        this.pauseBtn.setAttribute('aria-pressed', String(this.userPaused));
        this.pauseBtn.querySelectorAll('[data-motion-icon]').forEach((i) => { i.hidden = (i.dataset.motionIcon === 'pause') === this.userPaused; });
      }
      this.syncVideo();
    }

    syncVideo() {
      const motionOk = this.motionOk();
      // Only a slide with a video that can play shows the pointer, so the tap reads as a control
      this.slides.forEach((s) => s.classList.toggle('is-tappable', motionOk && !!s.querySelector(VIDEO)));
      if (this.pauseBtn) this.pauseBtn.hidden = !(motionOk && this.slides.some((s) => s.querySelector(VIDEO)));
      const allow = this.ready && this.onScreen && motionOk && !this.userPaused;
      this.slides.forEach((s, k) => {
        const video = s.querySelector(VIDEO);
        if (!video) return;
        if (allow && k === this.index) {
          if (!video.getAttribute('src')) {
            video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
            video.preload = 'auto';
            video.src = pickSource(video);
          }
          video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }
  }

  if (!customElements.get('aura-carousel')) customElements.define('aura-carousel', AuraCarousel);
})();
